import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  ClipboardIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import PermissionGate from '../components/PermissionGate';
import { adminApi } from '../services/api';

type SubjectListItem = {
  subject_id: number;
  subject_name: string;
  slug: string;
  updated_at: string;
};

type TopicListItem = {
  topic_id: number;
  topic_name: string;
  slug: string;
};

type ArticleListItem = {
  article_id: number;
  article_title: string;
  h1_heading: string;
  slug: string;
  content_type: string;
  publish_status: 'draft' | 'published' | 'archived' | string;
  updated_at: string;
  word_count: number;
  estimated_read_time: number;
  subject_slug?: string;
  subject_name?: string;
};

type ArticleDetail = {
  article_id: number;
  subject_id: number;
  language_code: string;
  slug: string;
  article_title: string;
  h1_heading: string | null;
  meta_title: string;
  meta_description: string;
  excerpt: string | null;
  content: string;
  focus_keyword: string | null;
  secondary_keywords: string | null;
  publish_status: 'draft' | 'published' | 'archived' | string;
  updated_at: string;
  word_count?: number;
  estimated_read_time?: number;
};

type ArticleDraft = {
  article_title: string;
  h1_heading: string;
  meta_title: string;
  meta_description: string;
  excerpt: string;
  content: string;
  focus_keyword: string;
  secondary_keywords: string;
};

function toOptionalNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : value;
}

function statusBadgeClasses(status: string) {
  switch (status) {
    case 'published':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
    case 'draft':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
    case 'archived':
      return 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
  }
}

export default function SubjectsHtmlEditor() {
  const queryClient = useQueryClient();

  const [language, setLanguage] = useState('en-IN');
  const [subjectSlug, setSubjectSlug] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [q, setQ] = useState('');

  const [page, setPage] = useState(0);
  const limit = 20;
  const offset = page * limit;

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const [original, setOriginal] = useState<ArticleDetail | null>(null);
  const [draft, setDraft] = useState<ArticleDraft>({
    article_title: '',
    h1_heading: '',
    meta_title: '',
    meta_description: '',
    excerpt: '',
    content: '',
    focus_keyword: '',
    secondary_keywords: '',
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmSlug, setDeleteConfirmSlug] = useState('');

  // New article modal state
  const [newArticleOpen, setNewArticleOpen] = useState(false);
  const [newArticle, setNewArticle] = useState({
    subject_id: 0,
    topic_id: 0,
    slug: '',
    article_title: '',
    h1_heading: '',
    meta_title: '',
    meta_description: '',
    excerpt: '',
    content: '',
    focus_keyword: '',
    secondary_keywords: '',
  });

  useEffect(() => {
    setPage(0);
  }, [language, subjectSlug, status, q]);

  useEffect(() => {
    setSelectedId(null);
    setShowPreview(false);
    setOriginal(null);
    setDeleteOpen(false);
    setDeleteConfirmSlug('');
  }, [language, subjectSlug]);

  const subjectsQuery = useQuery({
    queryKey: ['subjects-list', language],
    queryFn: () => adminApi.listSubjects({ language_code: language }).then((r) => r.data),
  });

  // Topics query for the new article modal - updates when subject changes
  const topicsQuery = useQuery({
    queryKey: ['subject-topics', newArticle.subject_id, language],
    queryFn: () =>
      adminApi.listSubjectTopics({ subject_id: newArticle.subject_id, language_code: language }).then((r) => r.data),
    enabled: newArticle.subject_id > 0,
  });

  const topicItems: TopicListItem[] = topicsQuery.data?.items || [];

  const articlesQuery = useQuery({
    queryKey: ['subjects-articles', status, q, language, subjectSlug, page],
    queryFn: () =>
      adminApi
        .listSubjectArticles({
          publish_status: (status || undefined) as any,
          q: q || undefined,
          language_code: language,
          subject_slug: subjectSlug || undefined,
          limit,
          offset,
        })
        .then((r) => r.data),
  });

  const items: ArticleListItem[] = articlesQuery.data?.items || [];
  const total: number = articlesQuery.data?.total ?? items.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const articleQuery = useQuery({
    queryKey: ['subject-article', selectedId],
    queryFn: () => adminApi.getSubjectArticle(selectedId as number).then((r) => r.data),
    enabled: !!selectedId,
  });

  useEffect(() => {
    if (!articleQuery.data) return;
    const a = articleQuery.data as ArticleDetail;
    setOriginal(a);
    setDraft({
      article_title: a.article_title || '',
      h1_heading: a.h1_heading || '',
      meta_title: a.meta_title || '',
      meta_description: a.meta_description || '',
      excerpt: a.excerpt || '',
      content: a.content || '',
      focus_keyword: a.focus_keyword || '',
      secondary_keywords: a.secondary_keywords || '',
    });
    setShowPreview(false);
    setDeleteOpen(false);
    setDeleteConfirmSlug('');
  }, [articleQuery.data?.article_id]);

  const isDirty = useMemo(() => {
    if (!original) return false;
    const normalized = {
      article_title: draft.article_title,
      h1_heading: toOptionalNull(draft.h1_heading),
      meta_title: draft.meta_title,
      meta_description: draft.meta_description,
      excerpt: toOptionalNull(draft.excerpt),
      content: draft.content,
      focus_keyword: toOptionalNull(draft.focus_keyword),
      secondary_keywords: toOptionalNull(draft.secondary_keywords),
    };
    return (
      normalized.article_title !== original.article_title ||
      (normalized.h1_heading || null) !== (original.h1_heading || null) ||
      normalized.meta_title !== original.meta_title ||
      normalized.meta_description !== original.meta_description ||
      (normalized.excerpt || null) !== (original.excerpt || null) ||
      normalized.content !== original.content ||
      (normalized.focus_keyword || null) !== (original.focus_keyword || null) ||
      (normalized.secondary_keywords || null) !== (original.secondary_keywords || null)
    );
  }, [draft, original]);

  const sanitizedPreview = useMemo(() => {
    return DOMPurify.sanitize(draft.content || '');
  }, [draft.content]);

  const buildPatch = (extra?: { publish_status?: 'draft' | 'published' | 'archived' }) => {
    if (!original) return null;
    const patch: any = {};

    const next = {
      article_title: draft.article_title,
      h1_heading: toOptionalNull(draft.h1_heading),
      meta_title: draft.meta_title,
      meta_description: draft.meta_description,
      excerpt: toOptionalNull(draft.excerpt),
      content: draft.content,
      focus_keyword: toOptionalNull(draft.focus_keyword),
      secondary_keywords: toOptionalNull(draft.secondary_keywords),
    };

    if (next.article_title !== original.article_title) patch.article_title = next.article_title;
    if ((next.h1_heading || null) !== (original.h1_heading || null)) patch.h1_heading = next.h1_heading;
    if (next.meta_title !== original.meta_title) patch.meta_title = next.meta_title;
    if (next.meta_description !== original.meta_description) patch.meta_description = next.meta_description;
    if ((next.excerpt || null) !== (original.excerpt || null)) patch.excerpt = next.excerpt;
    if (next.content !== original.content) patch.content = next.content;
    if ((next.focus_keyword || null) !== (original.focus_keyword || null)) patch.focus_keyword = next.focus_keyword;
    if ((next.secondary_keywords || null) !== (original.secondary_keywords || null))
      patch.secondary_keywords = next.secondary_keywords;

    if (extra?.publish_status && extra.publish_status !== original.publish_status) {
      patch.publish_status = extra.publish_status;
    }
    return patch;
  };

  const validateDraft = () => {
    if (!draft.article_title.trim()) return 'Article title is required';
    if (draft.article_title.length > 250) return 'Article title too long (max 250)';
    if (draft.h1_heading.length > 200) return 'H1 heading too long (max 200)';
    if (!draft.meta_title.trim()) return 'Meta title is required';
    if (draft.meta_title.length > 160) return 'Meta title too long (max 160)';
    if (!draft.meta_description.trim()) return 'Meta description is required';
    if (draft.meta_description.length > 200) return 'Meta description too long (max 200)';
    if (draft.focus_keyword.length > 100) return 'Focus keyword too long (max 100)';
    if (!draft.content.trim()) return 'HTML content is required';
    return null;
  };

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: any }) => adminApi.updateSubjectArticle(id, patch),
    onSuccess: async (_res, vars) => {
      toast.success('Saved');
      await queryClient.invalidateQueries({ queryKey: ['subjects-articles'], exact: false });
      await queryClient.invalidateQueries({ queryKey: ['subject-article', vars.id] });
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Save failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, confirm_slug }: { id: number; confirm_slug: string }) =>
      adminApi.deleteSubjectArticle(id, confirm_slug),
    onSuccess: async () => {
      toast.success('Deleted');
      setDeleteOpen(false);
      setDeleteConfirmSlug('');
      setSelectedId(null);
      setOriginal(null);
      await queryClient.invalidateQueries({ queryKey: ['subjects-articles'], exact: false });
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Delete failed');
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      subject_id: number;
      topic_id: number;
      language_code: string;
      slug: string;
      article_title: string;
      h1_heading?: string | null;
      meta_title: string;
      meta_description: string;
      excerpt?: string | null;
      content: string;
      focus_keyword?: string | null;
      secondary_keywords?: string | null;
    }) => adminApi.createSubjectArticle(data),
    onSuccess: async (res) => {
      toast.success('Article created');
      setNewArticleOpen(false);
      setNewArticle({
        subject_id: 0,
        topic_id: 0,
        slug: '',
        article_title: '',
        h1_heading: '',
        meta_title: '',
        meta_description: '',
        excerpt: '',
        content: '',
        focus_keyword: '',
        secondary_keywords: '',
      });
      await queryClient.invalidateQueries({ queryKey: ['subjects-articles'], exact: false });
      // Select the newly created article
      const newId = res.data?.article_id;
      if (newId) {
        setSelectedId(newId);
      }
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Create failed');
    },
  });

  const handleSave = (extra?: { publish_status?: 'draft' | 'published' | 'archived' }) => {
    if (!selectedId || !original) return;
    const validationError = validateDraft();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const patch = buildPatch(extra);
    if (!patch || Object.keys(patch).length === 0) {
      toast('No changes to save');
      return;
    }
    updateMutation.mutate({ id: selectedId, patch });
  };

  const handleCopyHtml = async () => {
    try {
      await navigator.clipboard.writeText(draft.content || '');
      toast.success('HTML copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s]+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 300);
  };

  const validateNewArticle = () => {
    if (!newArticle.subject_id) return 'Subject is required';
    if (!newArticle.topic_id) return 'Topic is required';
    if (!newArticle.slug.trim()) return 'Slug is required';
    if (!newArticle.slug.includes('/')) return 'Slug must include subject prefix (e.g., subject-slug/article-slug)';
    if (newArticle.slug.length > 300) return 'Slug too long (max 300)';
    if (!newArticle.article_title.trim()) return 'Article title is required';
    if (newArticle.article_title.length > 250) return 'Article title too long (max 250)';
    if (newArticle.h1_heading.length > 200) return 'H1 heading too long (max 200)';
    if (!newArticle.meta_title.trim()) return 'Meta title is required';
    if (newArticle.meta_title.length > 160) return 'Meta title too long (max 160)';
    if (!newArticle.meta_description.trim()) return 'Meta description is required';
    if (newArticle.meta_description.length > 200) return 'Meta description too long (max 200)';
    if (newArticle.focus_keyword.length > 100) return 'Focus keyword too long (max 100)';
    if (!newArticle.content.trim()) return 'HTML content is required';
    return null;
  };

  const handleCreateArticle = () => {
    const validationError = validateNewArticle();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    createMutation.mutate({
      subject_id: newArticle.subject_id,
      topic_id: newArticle.topic_id,
      language_code: language,
      slug: newArticle.slug.trim(),
      article_title: newArticle.article_title.trim(),
      h1_heading: newArticle.h1_heading.trim() || null,
      meta_title: newArticle.meta_title.trim(),
      meta_description: newArticle.meta_description.trim(),
      excerpt: newArticle.excerpt.trim() || null,
      content: newArticle.content,
      focus_keyword: newArticle.focus_keyword.trim() || null,
      secondary_keywords: newArticle.secondary_keywords.trim() || null,
    });
  };

  const handleOpenNewArticle = () => {
    // Pre-select the current subject filter if one is selected
    const currentSubject = subjectItems.find((s) => s.slug === subjectSlug);
    setNewArticle({
      subject_id: currentSubject?.subject_id || 0,
      topic_id: 0,
      slug: '',
      article_title: '',
      h1_heading: '',
      meta_title: '',
      meta_description: '',
      excerpt: '',
      content: '',
      focus_keyword: '',
      secondary_keywords: '',
    });
    setNewArticleOpen(true);
  };

  const subjectItems: SubjectListItem[] = subjectsQuery.data?.items || [];

  return (
    <PermissionGate
      requireAdmin
      fallback={
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          Admin access required.
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Subjects HTML Editor</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              View, edit, publish, or delete subject articles as raw HTML.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenNewArticle}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              title="Create new article"
            >
              <PlusIcon className="h-4 w-4" />
              New Article
            </button>
            <button
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['subjects-list', language] });
                queryClient.invalidateQueries({ queryKey: ['subjects-articles'], exact: false });
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              title="Refresh lists"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Language</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
              >
                <option value="en-IN">English (en-IN)</option>
                <option value="hi-IN">Hindi (hi-IN)</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Subject</label>
              <select
                value={subjectSlug}
                onChange={(e) => setSubjectSlug(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
              >
                <option value="">All subjects</option>
                {subjectItems.map((s) => (
                  <option key={s.subject_id} value={s.slug}>
                    {s.subject_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
              >
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Search</label>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                placeholder="Title / H1 / slug"
              />
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <div>
              {articlesQuery.isLoading ? 'Loading…' : `${total} articles`}
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900"
              >
                Prev
              </button>
              <span>
                Page {page + 1} / {totalPages}
              </span>
              <button
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Split View */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          {/* List */}
          <div className="lg:col-span-2">
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Articles</h2>
              </div>
              <div className="max-h-[70vh] overflow-auto">
                {articlesQuery.isLoading ? (
                  <div className="p-4 text-sm text-gray-500">Loading…</div>
                ) : items.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">No articles found.</div>
                ) : (
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {items.map((a) => {
                      const isSelected = a.article_id === selectedId;
                      const displayTitle = a.h1_heading || a.article_title || a.slug;
                      return (
                        <li
                          key={a.article_id}
                          className={`cursor-pointer px-4 py-3 transition-colors ${
                            isSelected
                              ? 'bg-blue-50 dark:bg-blue-900/20'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                          }`}
                          onClick={() => setSelectedId(a.article_id)}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate font-medium text-gray-900 dark:text-white">
                                {displayTitle}
                              </div>
                              <div className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                                {a.subject_name ? `${a.subject_name} • ` : ''}
                                {a.slug}
                              </div>
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {a.word_count} words • {a.estimated_read_time}m
                              </div>
                            </div>
                            <span
                              className={`mt-0.5 inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClasses(
                                a.publish_status
                              )}`}
                            >
                              {a.publish_status}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Editor */}
          <div className="lg:col-span-3">
            {!selectedId ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                Select an article to edit HTML.
              </div>
            ) : articleQuery.isLoading ? (
              <div className="rounded-xl border border-gray-200 bg-white p-6 text-gray-500 dark:border-gray-700 dark:bg-gray-800">
                Loading article…
              </div>
            ) : !original ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800 dark:border-red-900/30 dark:bg-red-900/20 dark:text-red-200">
                Failed to load article.
              </div>
            ) : (
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                      {original.slug}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className={`rounded-full px-2 py-0.5 ${statusBadgeClasses(original.publish_status)}`}>
                        {original.publish_status}
                      </span>
                      <span>{original.word_count || 0} words</span>
                      <span>•</span>
                      <span>{original.estimated_read_time || 0}m read</span>
                      <span>•</span>
                      <span className={isDirty ? 'text-yellow-700 dark:text-yellow-300' : ''}>
                        {isDirty ? 'Unsaved changes' : 'Saved'}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={handleCopyHtml}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                      title="Copy HTML"
                    >
                      <ClipboardIcon className="h-4 w-4" />
                      Copy
                    </button>
                    <button
                      onClick={() => setShowPreview((v) => !v)}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                      title="Toggle preview"
                    >
                      {showPreview ? (
                        <>
                          <EyeSlashIcon className="h-4 w-4" />
                          Hide preview
                        </>
                      ) : (
                        <>
                          <EyeIcon className="h-4 w-4" />
                          Preview
                        </>
                      )}
                    </button>
                    <button
                      disabled={updateMutation.isPending}
                      onClick={() => handleSave()}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      disabled={updateMutation.isPending}
                      onClick={() => handleSave({ publish_status: 'published' })}
                      className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      Publish
                    </button>
                    <button
                      disabled={updateMutation.isPending}
                      onClick={() => handleSave({ publish_status: 'draft' })}
                      className="rounded-lg bg-yellow-600 px-4 py-2 text-sm font-medium text-white hover:bg-yellow-700 disabled:opacity-50"
                    >
                      Draft
                    </button>
                    <button
                      disabled={updateMutation.isPending}
                      onClick={() => handleSave({ publish_status: 'archived' })}
                      className="rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                    >
                      Archive
                    </button>
                    <button
                      disabled={deleteMutation.isPending}
                      onClick={() => setDeleteOpen(true)}
                      className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:bg-gray-900 dark:text-red-200 dark:hover:bg-red-900/20"
                      title="Delete article"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400">Article title</label>
                    <input
                      value={draft.article_title}
                      onChange={(e) => setDraft((d) => ({ ...d, article_title: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      maxLength={250}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400">
                      H1 heading <span className="text-xs">(optional)</span>
                    </label>
                    <input
                      value={draft.h1_heading}
                      onChange={(e) => setDraft((d) => ({ ...d, h1_heading: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400">
                      Meta title <span className="text-xs">({draft.meta_title.length}/160)</span>
                    </label>
                    <input
                      value={draft.meta_title}
                      onChange={(e) => setDraft((d) => ({ ...d, meta_title: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      maxLength={160}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400">
                      Focus keyword <span className="text-xs">(optional, {draft.focus_keyword.length}/100)</span>
                    </label>
                    <input
                      value={draft.focus_keyword}
                      onChange={(e) => setDraft((d) => ({ ...d, focus_keyword: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      maxLength={100}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-500 dark:text-gray-400">
                      Meta description <span className="text-xs">({draft.meta_description.length}/200)</span>
                    </label>
                    <textarea
                      value={draft.meta_description}
                      onChange={(e) => setDraft((d) => ({ ...d, meta_description: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      rows={3}
                      maxLength={200}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-500 dark:text-gray-400">
                      Secondary keywords <span className="text-xs">(optional)</span>
                    </label>
                    <input
                      value={draft.secondary_keywords}
                      onChange={(e) => setDraft((d) => ({ ...d, secondary_keywords: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      placeholder="Comma-separated"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm text-gray-500 dark:text-gray-400">
                      Excerpt <span className="text-xs">(optional)</span>
                    </label>
                    <textarea
                      value={draft.excerpt}
                      onChange={(e) => setDraft((d) => ({ ...d, excerpt: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      rows={2}
                    />
                  </div>

                  <div className={showPreview ? 'md:col-span-1' : 'md:col-span-2'}>
                    <label className="text-sm text-gray-500 dark:text-gray-400">HTML content</label>
                    <textarea
                      value={draft.content}
                      onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-xs leading-relaxed dark:border-gray-600 dark:bg-gray-900"
                      rows={22}
                    />
                  </div>

                  {showPreview && (
                    <div className="md:col-span-1">
                      <label className="text-sm text-gray-500 dark:text-gray-400">Preview (sanitized)</label>
                      <div className="mt-1 max-h-[520px] overflow-auto rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                        <div
                          className="prose max-w-none dark:prose-invert"
                          dangerouslySetInnerHTML={{ __html: sanitizedPreview }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete modal */}
      {deleteOpen && original && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setDeleteOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete article</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This will permanently delete <span className="font-mono">{original.slug}</span>. Published articles must be
              moved to draft or archived first.
            </p>

            <div className="mt-4">
              <label className="text-sm text-gray-500 dark:text-gray-400">
                Type the slug to confirm: <span className="font-mono">{original.slug}</span>
              </label>
              <input
                value={deleteConfirmSlug}
                onChange={(e) => setDeleteConfirmSlug(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm dark:border-gray-600 dark:bg-gray-900"
                placeholder={original.slug}
              />
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setDeleteOpen(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                disabled={deleteConfirmSlug !== original.slug || deleteMutation.isPending}
                onClick={() =>
                  deleteMutation.mutate({ id: original.article_id, confirm_slug: deleteConfirmSlug })
                }
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Article modal */}
      {newArticleOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setNewArticleOpen(false)}
        >
          <div
            className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create New Article</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              New articles are created as drafts. You can publish them after creation.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  Subject <span className="text-red-500">*</span>
                </label>
                <select
                  value={newArticle.subject_id}
                  onChange={(e) => {
                    const newSubjectId = Number(e.target.value);
                    const newSubject = subjectItems.find((s) => s.subject_id === newSubjectId);
                    const newSubjectSlug = newSubject?.slug || '';

                    setNewArticle((a) => {
                      // Extract the article part from the existing slug if it has a prefix
                      const existingSlug = a.slug;
                      let articlePart = existingSlug;
                      if (existingSlug.includes('/')) {
                        articlePart = existingSlug.split('/').slice(1).join('/');
                      }
                      // Generate new full slug with new subject prefix
                      const newSlug = newSubjectSlug && articlePart ? `${newSubjectSlug}/${articlePart}` : articlePart;
                      return { ...a, subject_id: newSubjectId, topic_id: 0, slug: newSlug };
                    });
                  }}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                >
                  <option value={0}>Select a subject</option>
                  {subjectItems.map((s) => (
                    <option key={s.subject_id} value={s.subject_id}>
                      {s.subject_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  Topic <span className="text-red-500">*</span>
                </label>
                <select
                  value={newArticle.topic_id}
                  onChange={(e) => setNewArticle((a) => ({ ...a, topic_id: Number(e.target.value) }))}
                  disabled={!newArticle.subject_id || topicsQuery.isLoading}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-100 dark:border-gray-600 dark:bg-gray-900 dark:disabled:bg-gray-700"
                >
                  <option value={0}>
                    {!newArticle.subject_id
                      ? 'Select a subject first'
                      : topicsQuery.isLoading
                        ? 'Loading topics...'
                        : topicItems.length === 0
                          ? 'No topics available'
                          : 'Select a topic'}
                  </option>
                  {topicItems.map((t) => (
                    <option key={t.topic_id} value={t.topic_id}>
                      {t.topic_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">Language</label>
                <input
                  value={language === 'en-IN' ? 'English (en-IN)' : 'Hindi (hi-IN)'}
                  disabled
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700"
                />
              </div>

              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  Article title <span className="text-red-500">*</span>
                  <span className="ml-1 text-xs">({newArticle.article_title.length}/250)</span>
                </label>
                <input
                  value={newArticle.article_title}
                  onChange={(e) => {
                    const title = e.target.value;
                    const selectedSubject = subjectItems.find((s) => s.subject_id === newArticle.subject_id);
                    const subjectSlugPrefix = selectedSubject?.slug || '';
                    const articleSlugPart = generateSlug(title);
                    const fullSlug = subjectSlugPrefix ? `${subjectSlugPrefix}/${articleSlugPart}` : articleSlugPart;
                    setNewArticle((a) => ({
                      ...a,
                      article_title: title,
                      // Only auto-generate if slug is empty or was auto-generated (contains the subject prefix)
                      slug: !a.slug || a.slug.startsWith(subjectSlugPrefix + '/') ? fullSlug : a.slug,
                    }));
                  }}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                  maxLength={250}
                  placeholder="e.g., Article 370 - Special Status to Jammu & Kashmir"
                />
              </div>

              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  H1 heading <span className="text-xs">(optional, {newArticle.h1_heading.length}/200)</span>
                </label>
                <input
                  value={newArticle.h1_heading}
                  onChange={(e) => setNewArticle((a) => ({ ...a, h1_heading: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                  maxLength={200}
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  Slug (URL path) <span className="text-red-500">*</span>
                  <span className="ml-1 text-xs">({newArticle.slug.length}/300)</span>
                </label>
                <input
                  value={newArticle.slug}
                  onChange={(e) => setNewArticle((a) => ({ ...a, slug: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm dark:border-gray-600 dark:bg-gray-900"
                  maxLength={300}
                  placeholder="polity-upsc-notes/article-370-special-status"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Format: <span className="font-mono">subject-slug/article-slug</span>. Auto-generated from title with subject prefix.
                </p>
                {newArticle.slug && (
                  <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                    Preview: <span className="font-mono">https://clarityupsc.com/{newArticle.slug}</span>
                  </p>
                )}
                {newArticle.slug && !newArticle.slug.includes('/') && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    ⚠ Slug must include subject prefix (e.g., polity-upsc-notes/article-slug)
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  Meta title <span className="text-red-500">*</span>
                  <span className="ml-1 text-xs">({newArticle.meta_title.length}/160)</span>
                </label>
                <input
                  value={newArticle.meta_title}
                  onChange={(e) => setNewArticle((a) => ({ ...a, meta_title: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                  maxLength={160}
                  placeholder="Article 370 - Special Status | Polity Notes for UPSC IAS 2025"
                />
              </div>

              <div>
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  Focus keyword <span className="text-xs">(optional, {newArticle.focus_keyword.length}/100)</span>
                </label>
                <input
                  value={newArticle.focus_keyword}
                  onChange={(e) => setNewArticle((a) => ({ ...a, focus_keyword: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                  maxLength={100}
                  placeholder="Article 370 UPSC"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  Meta description <span className="text-red-500">*</span>
                  <span className="ml-1 text-xs">({newArticle.meta_description.length}/200)</span>
                </label>
                <textarea
                  value={newArticle.meta_description}
                  onChange={(e) => setNewArticle((a) => ({ ...a, meta_description: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                  rows={2}
                  maxLength={200}
                  placeholder="Article 370 explained for UPSC IAS. Covers special status, abrogation, SC verdict. Updated for 2025."
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  Secondary keywords <span className="text-xs">(optional, comma-separated)</span>
                </label>
                <input
                  value={newArticle.secondary_keywords}
                  onChange={(e) => setNewArticle((a) => ({ ...a, secondary_keywords: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                  placeholder="Jammu Kashmir, Article 35A, Supreme Court verdict"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  Excerpt <span className="text-xs">(optional)</span>
                </label>
                <textarea
                  value={newArticle.excerpt}
                  onChange={(e) => setNewArticle((a) => ({ ...a, excerpt: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                  rows={2}
                  placeholder="Short summary for previews..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm text-gray-500 dark:text-gray-400">
                  HTML content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newArticle.content}
                  onChange={(e) => setNewArticle((a) => ({ ...a, content: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-xs leading-relaxed dark:border-gray-600 dark:bg-gray-900"
                  rows={12}
                  placeholder="<h2>Introduction</h2>\n<p>Your article content here...</p>"
                />
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setNewArticleOpen(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                disabled={createMutation.isPending}
                onClick={handleCreateArticle}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating...' : 'Create Article'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PermissionGate>
  );
}
